import { QUEUE_EMPLOYEE_TYPE_OPTIONS, type QueueEmployeeType } from "../../utils/onboarding-queue.ts";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useBreadcrumbs } from "../../context/breadcrumb/breadcrumb-context.tsx";
import { showError } from "../../utils/toast.ts";
import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { EmptyStateMessage } from "../../components/shared/empty-state-message/empty-state-message.tsx";
import { HrApprovalModal } from "../../components/features/hr-setup/modals/hr-approval-modal.tsx";
import { useHrSetupList } from "../../components/features/hr-setup/hooks/use-hr-setup-list.ts";
import type { HrSetupEmployeeRow } from "../../components/features/hr-setup/schemas/hr-setup-employee.schema.ts";
import { formatOnboardEmployeeName } from "../../components/features/hr-setup/utils/format-onboard-employee-name.ts";
import {
  ResponsiveDataTable,
  type ResponsiveColumn,
  Spinner,
  Select,
} from "../../components/ui/index.ts";
import type { PageProps } from "../../types/page.ts";

import styles from "./hr-setup-page.module.css";
import { Eye } from "lucide-react";
import { useSqeLabs } from "../../hooks/use-sqe-labs.ts";

type HrSetupTableRow = HrSetupEmployeeRow & { id: string } & Record<string, unknown>;

export default function HrSetupPage({ title, description }: PageProps) {
  const isSqe = useSqeLabs();
  const { setBreadcrumbs } = useBreadcrumbs();
  const [employeeType, setEmployeeType] = useState<QueueEmployeeType>("ALL");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<HrSetupEmployeeRow | null>(null);

  const { rows, totalElements, loading, error, refetch } = useHrSetupList(page, pageSize, employeeType);

  useEffect(() => {
    setBreadcrumbs([{ label: title ?? "Employee Setup" }]);
    return () => setBreadcrumbs([]);
  }, [setBreadcrumbs, title]);

  useEffect(() => {
    if (error) showError(error);
  }, [error]);

  const tableRows: HrSetupTableRow[] = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        id: String(row.id),
      })),
    [rows],
  );

  const openEmployee = useCallback((row: HrSetupEmployeeRow) => {
    setSelectedEmployee(row);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setSelectedEmployee(null);
  }, []);

  const handlePageSizeChange = useCallback((next: number) => {
    setPageSize(next);
    setPage(1);
  }, []);

  const columns = useMemo<ResponsiveColumn<HrSetupTableRow>[]>(
    () => [
      {
        id: "name",
        header: "Employee Name",
        sortable: true,
        truncate: true,
        render: (row) => (
          <span className="body-sm">{formatOnboardEmployeeName(row)}</span>
        ),
        getSortValue: (row) => formatOnboardEmployeeName(row),
        getTitleText: (row) => formatOnboardEmployeeName(row),
      },
      {
        id: "email",
        header: "Email",
        accessor: "personalEmail",
        sortable: true,
        truncate: true,
      },
      {
        id: "phone",
        header: "Phone Number",
        accessor: "phoneNumber",
        sortable: true,
        nowrap: true,
      },
      {
        id: "employeeType",
        header: "Employee Type",
        render: (row) => isSqe ? "SQE Labs" : row.consultantId != null || row.employeeType === "CONSULTANT" ? "Consultant" : "Regular",
      },
      
      {
        id: "actions",
        header: "View",
        sortable: false,
        nowrap: true,
        render: (row) => (
          <button
            type="button"
            className={styles.iconBtn}
            aria-label={`View ${formatOnboardEmployeeName(row)}`}
            onClick={() => openEmployee(row)}
          >
            <Eye size={18} strokeWidth={2} aria-hidden />
          </button>
        ),
      },
    ],
    [openEmployee, isSqe],
  );

  return (
    <PageShell>
      <div className={styles.queueFilters}>
        {!isSqe && <Select id="hr-setup-employee-type" label="Employee Type" options={QUEUE_EMPLOYEE_TYPE_OPTIONS} value={employeeType} onChange={(value) => { setEmployeeType(value as QueueEmployeeType); setPage(1); }} />}
      </div>
      {loading ? (
        <Spinner />
      ) : (
        <div className={styles.tableSection}>
          <ResponsiveDataTable<HrSetupTableRow>
            serialNumber
            columns={columns}
            data={tableRows}
            defaultSortKey="name"
            defaultSortDir="asc"
            aria-label="HR setup employees"
            emptyState={<EmptyStateMessage message="No records available." />}
            pagination
            page={page}
            pageSize={pageSize}
            totalItems={totalElements}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[5, 10, 20, 30, 40]}
            totalLabel={
              <>
                Total Requests: <strong>{totalElements}</strong>
              </>
            }
          />
        </div>
      )}

      <HrApprovalModal
        open={modalOpen}
        onClose={closeModal}
        employee={selectedEmployee}
        onSuccess={() => void refetch()}
      />
    </PageShell>
  );
}
